import React, { useState, useEffect, useMemo } from "react";
import { DatePicker, Input, Select, Button, Modal, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Table from "../components/Table";
import DashboardBox from "../components/DashboardBox";
import { useFetchAuditReport } from "../hooks/useFetch";
// import { useFetchCase3 } from "../hooks/useFetch";
import { UseFetchCaseSetting } from "../../../../modules/admin/AddCaseSetting/hooks/useFetch";
import {
  INPUT_CASE_TYPE_OPTIONS,
  INPUT_CASE_TO_CASE1,
} from "../../../../data/constant";
const { Option } = Select;
const { RangePicker } = DatePicker

const AuditPage = () => {
  const today = dayjs();
  const companyId = sessionStorage.getItem("activeCompanyId");
  const [selectedDate, setSelectedDate] = useState([dayjs(), dayjs()]);
  const [type, setType] = useState("");
  const [userIdSearch, setUserIdSearch] = useState("");
  const [caseNameSearch, setCaseNameSearch] = useState("");
  // แยกแอดมิน 2 ช่องให้ตรงกับ 2 คอลัมน์ในตาราง (เดิมมีช่องเดียวแต่ค้นแค่ finish_by
  // → ค้นชื่อคนที่ "ส่ง" เคสแล้วไม่เจอทั้งที่โชว์อยู่ในตาราง)
  const [createAdminSearch, setCreateAdminSearch] = useState("");
  const [finishAdminSearch, setFinishAdminSearch] = useState("");
  // case2/case3 เก็บเป็น "id" (เดิมเก็บชื่อ → backend เทียบแบบ substring → ได้ผลเกิน)
  const [case2Search, setCase2Search] = useState("");
  const [case3Search, setCase3Search] = useState("");
  const [auditStatusSearch, setAuditStatusSearch] = useState("");
  const [data, setAuditData] = useState([]);
  const [dashboardData, setDashboardData] = useState([]);
  // const { data: case3Data } = useFetchCase3();
  const { data: case2Data } = UseFetchCaseSetting(companyId);
  const { mutateAsync: mutateAsyncAuditReport, isLoading: isLoadingAuditReport } = useFetchAuditReport();
  // const {
  //   data: auditData,
  //   isLoading,
  //   isError,
  //   refetch,
  // } = useFetchAuditReport(formattedDate);


  // ===== ตัวเลือกแบบเชื่อมโยงกัน (cascade): Case Name → Case 2 → Case 3 =====
  // CaseTopic.type ผูกกับ Case Name อยู่แล้ว ('1'=deposit, '2'=withdraw, ...) ตาม INPUT_CASE_TO_CASE1
  // และ Case 3 ก็คือ subcases ของ Case 2 ที่เลือก — เดิมไม่ได้ใช้ความสัมพันธ์นี้เลย
  const case1Type = caseNameSearch ? INPUT_CASE_TO_CASE1[caseNameSearch] : null;

  const case2Options = useMemo(() => {
    if (!Array.isArray(case2Data)) return [];
    if (!case1Type) return case2Data; // ยังไม่เลือก Case Name → เลือกได้ทุกอัน
    return case2Data.filter((ct) => String(ct.type) === String(case1Type));
  }, [case2Data, case1Type]);

  const case3Options = useMemo(() => {
    if (!case2Search || !Array.isArray(case2Data)) return [];
    const topic = case2Data.find((ct) => String(ct.id) === String(case2Search));
    return topic?.subcases ?? [];
  }, [case2Data, case2Search]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const EMPTY_FILTERS = {
    selectedDate: [today, today],
    type: "",
    userIdSearch: "",
    caseNameSearch: "",
    createAdminSearch: "",
    finishAdminSearch: "",
    case2Search: "",
    case3Search: "",
    auditStatusSearch: "",
  };

  const handleClearFilters = () => {
    setSelectedDate(EMPTY_FILTERS.selectedDate);
    setType(EMPTY_FILTERS.type);
    setUserIdSearch(EMPTY_FILTERS.userIdSearch);
    setCaseNameSearch(EMPTY_FILTERS.caseNameSearch);
    setCreateAdminSearch(EMPTY_FILTERS.createAdminSearch);
    setFinishAdminSearch(EMPTY_FILTERS.finishAdminSearch);
    setCase2Search(EMPTY_FILTERS.case2Search);
    setCase3Search(EMPTY_FILTERS.case3Search);
    setAuditStatusSearch(EMPTY_FILTERS.auditStatusSearch);
    // ค้นหาใหม่ด้วยค่าที่ล้างแล้วทันที — เดิมล้างแต่ช่องกรอก ตารางยังโชว์ผลเก่า = ข้อมูลไม่ตรงกับตัวกรอง
    // ต้องส่งค่าเข้าไปตรง ๆ ไม่อ่านจาก state เพราะ setState ยังไม่มีผลในรอบนี้
    runSearch(EMPTY_FILTERS);
  };

  // แยก runSearch ออกมารับค่า filter ตรง ๆ → เรียกได้ทั้งจากปุ่มค้นหา (ใช้ state ปัจจุบัน)
  // และจากปุ่มล้าง (ใช้ค่าที่เพิ่งล้าง) โดยไม่ติดปัญหา state ยังไม่อัปเดต
  const runSearch = async (f) => {
    try {
      const trim = (v) => (v == null ? "" : String(v).trim());

      const payload = {
        start_date: f.selectedDate[0].format("YYYY-MM-DD"),
        end_date: f.selectedDate[1].format("YYYY-MM-DD"),
      };

      const typeTrimmed = trim(f.type);
      if (typeTrimmed) payload.type = typeTrimmed;

      const userIdTrimmed = trim(f.userIdSearch);
      if (userIdTrimmed) payload.user_id = userIdTrimmed;

      const caseNameTrimmed = trim(f.caseNameSearch);
      if (caseNameTrimmed) payload.case_name = caseNameTrimmed;

      const createAdminTrimmed = trim(f.createAdminSearch);
      if (createAdminTrimmed) payload.create_admin_name = createAdminTrimmed;

      const finishAdminTrimmed = trim(f.finishAdminSearch);
      if (finishAdminTrimmed) payload.finish_admin_name = finishAdminTrimmed;

      // ส่งเป็น id → backend เทียบ dataManage.case2/case3 แบบตรงตัว (ไม่ใช่ substring ของชื่อ)
      const case2Trimmed = trim(f.case2Search);
      if (case2Trimmed) payload.case2 = case2Trimmed;

      const case3Trimmed = trim(f.case3Search);
      if (case3Trimmed) payload.case3 = case3Trimmed;

      // สถานะที่ admin ตรวจเอง: unaudited / Pass / Fail
      const auditStatusTrimmed = trim(f.auditStatusSearch);
      if (auditStatusTrimmed) payload.auditStatus = auditStatusTrimmed;

      const data = await mutateAsyncAuditReport(payload);
      setAuditData(data?.result?.data ?? []);
      setDashboardData(data?.result?.dashboard ?? []);
    } catch (error) {
      await message.destroy();
      message.error("Failed To Load Audit Report..");
      throw error;
    }
  };

  const handleSearch = () =>
    runSearch({
      selectedDate,
      type,
      userIdSearch,
      caseNameSearch,
      createAdminSearch,
      finishAdminSearch,
      case2Search,
      case3Search,
      auditStatusSearch,
    });

  const handleExportClick = () => {
    if (!data || data.length === 0) {
      message.warning("ไม่มีข้อมูลสำหรับ export");
      return;
    }

    Modal.confirm({
      title: "ยืนยันการ Export",
      content: `คุณต้องการ export ข้อมูล ${data.length} รายการ ใช่หรือไม่?`,
      okText: "ยืนยัน",
      cancelText: "ยกเลิก",
      onOk: () => {
        handleExport();
      },
    });
  };

  const handleExport = () => {
    // ============================================
    // ส่วนจัดเรียงข้อมูล - คุณสามารถแก้ไขส่วนนี้ได้ตามต้องการ
    // ============================================
    let sortedData = [...data];

    // ตัวอย่างการจัดเรียงข้อมูล (comment ไว้ คุณสามารถ uncomment และแก้ไขได้)
    // sortedData = sortedData.sort((a, b) => {
    //   // ตัวอย่าง: เรียงตาม User ID
    //   return a.userId - b.userId;
    // });

    // หรือเรียงตามวันที่
    sortedData = sortedData.sort((a, b) => {
      return new Date(a.createdate) - new Date(b.createdate);
    });

    // หรือเรียงตาม Amount
    // sortedData = sortedData.sort((a, b) => {
    //   return parseFloat(b.amount || 0) - parseFloat(a.amount || 0);
    // });

    // ============================================
    // ส่วนสร้าง Excel Sheet
    // ============================================

    // กำหนด headers (ชื่อคอลัมน์)
    const headers = [
      "เวลา (Input)",
      "แอดมิน (Output)",
      "Case Name",
      "Case 2",
      "Case 3",
      "User ID",
      "เวลา เริมต้น - สิ้นสุด",
      "Amount",
      "Note",
      "Transaction ID"
    ];

    // แปลงข้อมูลเป็นรูปแบบที่ Excel ต้องการ
    const excelData = sortedData.map((item) => [
      item.createdate ? dayjs(item.createdate).format("DD/MM/YYYY HH:mm:ss") : "-",
      item.finish_by_Name || "-",
      item.caseName || "-",
      item.case2Display || "-",
      item.case3Display || "-",
      item.userId || "-",
      item.startEndDate || "-",
      item.amount || "0",
      item.note || "-",
      item.ts_id || "-"
    ]);

    // รวม headers กับข้อมูล
    const worksheetData = [headers, ...excelData];

    // สร้าง worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // กำหนดความกว้างของคอลัมน์ (optional)
    worksheet["!cols"] = [
      { wch: 20 }, // เวลา (Input)
      { wch: 20 }, // แอดมิน (Output)  
      { wch: 15 }, // Case Name
      { wch: 15 }, // Case 2
      { wch: 20 }, // Case 3
      { wch: 15 }, // User ID  
      { wch: 20 }, // เวลา เริมต้น - สิ้นสุด
      { wch: 12 }, // Amount
      { wch: 50 }, // Note
      { wch: 40 }, // Transaction ID
    ];

    // สร้าง workbook และเพิ่ม worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "รายงานจัดการเคส");

    // แปลงเป็นไฟล์ Excel และดาวน์โหลด
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

    const fileName = `audit-report-${selectedDate[0].format("YYYY-MM-DD")}-${selectedDate[1].format("YYYY-MM-DD")}.xlsx`;
    saveAs(blob, fileName);

    message.success("Export ข้อมูลสำเร็จ");
  };

  return (
    <div style={{ padding: 16, background: "#f5f5f5", minHeight: "100vh", marginLeft: 16 }}>
      {/* Title */}
      <div style={{ marginBottom: 20 }}>
        <h1
          style={{
            margin: 0,
            fontSize: "2rem",
            fontWeight: "bold",
            color: "#212121",
            letterSpacing: "0.5px",
            lineHeight: 1.2,
          }}
        >
          รายงานจัดการเคส
        </h1>
      </div>

      {/* Filter Section */}
      <div
        style={{
          background: "#fff",
          borderRadius: 10,
          padding: 20,
          marginBottom: 20,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16,
            alignItems: "end",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
              }}
            >
              วันที่
            </label>
            <RangePicker
              value={selectedDate}
              onChange={setSelectedDate}
              format="DD/MM/YYYY"
              style={{ width: "100%" }}
              allowClear={false}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
              }}
            >
              ประเภท
            </label>
            <Select
              value={type}
              onChange={setType}
              placeholder="เลือกประเภท"
              allowClear
              style={{ width: "100%" }}
            >
              <Option value="เติม">เติม</Option>
              <Option value="ไม่เติม">ไม่เติม</Option>
              {/* <Option value="เติม / ไม่เติม">เติม / ไม่เติม</Option> */}
            </Select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
              }}
            >
              ค้นหา "User ID"
            </label>
            <Input
              value={userIdSearch}
              onChange={(e) => setUserIdSearch(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
              }}
            >
              ค้นหา "แอดมิน (ส่ง)"
            </label>
            <Input
              value={createAdminSearch}
              onChange={(e) => setCreateAdminSearch(e.target.value)}
              onPressEnter={handleSearch}
              placeholder="ชื่อคนที่ส่งเคส"
              allowClear
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
              }}
            >
              ค้นหา "แอดมิน (จัดการ)"
            </label>
            <Input
              value={finishAdminSearch}
              onChange={(e) => setFinishAdminSearch(e.target.value)}
              onPressEnter={handleSearch}
              placeholder="ชื่อคนที่ปิดเคส"
              allowClear
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
              }}
            >
              สถานะตรวจ
            </label>
            <Select
              value={auditStatusSearch || undefined}
              onChange={(value) => setAuditStatusSearch(value ?? "")}
              placeholder="ทั้งหมด"
              allowClear
              style={{ width: "100%" }}
            >
              <Option value="unaudited">ยังไม่ตรวจ</Option>
              <Option value="Pass">ตรวจแล้ว - ผ่าน</Option>
              <Option value="Fail">ตรวจแล้ว - ผิด</Option>
            </Select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
              }}
            >
              ค้นหา "Case Name"
            </label>
            <Select
              value={caseNameSearch || undefined}
              onChange={(value) => {
                // เปลี่ยนตัวแม่ → ล้างตัวลูก (Case 2 ของประเภทเดิมใช้กับประเภทใหม่ไม่ได้)
                setCaseNameSearch(value ?? "");
                setCase2Search("");
                setCase3Search("");
              }}
              placeholder="ทั้งหมด"
              allowClear
              style={{ width: "100%" }}
            >
              {INPUT_CASE_TYPE_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
              }}
            >
              ค้นหา "Case 2"
            </label>
            <Select
              value={case2Search || undefined}
              onChange={(value) => {
                setCase2Search(value ?? "");
                setCase3Search(""); // เปลี่ยน Case 2 → subcases ชุดใหม่ ตัวเก่าใช้ไม่ได้
              }}
              placeholder={caseNameSearch ? "ทั้งหมด" : "เลือก Case Name ก่อน (หรือเลือกจากทั้งหมด)"}
              allowClear
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
            >
              {/* value = id → backend เทียบตรงตัว · เดิมส่งชื่อแล้วโดนเทียบแบบ substring จึงได้ผลเกิน */}
              {case2Options.map((item) => (
                <Option key={item.id} value={String(item.id)}>
                  {item.casename}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: "#333",
              }}
            >
              ค้นหา "Case 3"
            </label>
            {/* เดิมเป็นช่องพิมพ์เปล่า ทั้งที่ subcases ของ Case 2 มีอยู่ในมือแล้ว
                → ต้องเดาชื่อเอง + โดนเทียบแบบ substring ("ฝาก" ได้ "ฝากผิดบัญชี" ติดมา) */}
            <Select
              value={case3Search || undefined}
              onChange={(value) => setCase3Search(value ?? "")}
              placeholder={case2Search ? "ทั้งหมด" : "เลือก Case 2 ก่อน"}
              disabled={!case2Search}
              allowClear
              style={{ width: "100%" }}
              showSearch
              optionFilterProp="children"
            >
              {case3Options.map((item) => (
                <Option key={item.id} value={String(item.id)}>
                  {item.casename}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <Button
              type="primary"
              onClick={handleSearch}
              style={{
                width: "100%",
                height: "32px",
              }}
            >
              ค้นหา
            </Button>
          </div>

          <div>
            <Button
              onClick={handleClearFilters}
              style={{
                width: "100%",
                height: "32px",
              }}
            >
              ล้างตัวกรอง
            </Button>
          </div>

          <div>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportClick}
              style={{
                width: "100%",
                height: "32px",
              }}
            >
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Section */}
      <div
        style={{
          display: "flex",
          gap: 20,
          marginBottom: 20,
          alignItems: "center",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            padding: "16px 24px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <h2 style={{ fontSize: 18, fontWeight: "bold", marginBottom: 10, color: "#333" }}>ทั้งหมด</h2>
          <div
            style={{
              display: "flex",
              gap: 30,
            }}
          >
            <div>
              <div style={{ fontSize: 14, color: "#666", marginBottom: 4 }}>
                เคสทั้งหมด :
              </div>
              <div style={{ fontSize: 28, fontWeight: "bold", color: "#212121" }}>
                {data?.length || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 14, color: "#666", marginBottom: 4 }}>
                ยอดรวม :
              </div>
              <div style={{ fontSize: 28, fontWeight: "bold", color: "#212121" }}>
                {(data && data.length > 0)
                  ? data.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2)
                  : "0.00"}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            padding: "16px 24px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <h2 style={{ fontSize: 18, fontWeight: "bold", marginBottom: 10, color: "#333" }}>Deposit</h2>
          <div
            style={{
              display: "flex",
              gap: 30,
            }}
          >
            <div>
              <div style={{ fontSize: 14, color: "#666", marginBottom: 4 }}>
                เคสทั้งหมด :
              </div>
              <div style={{ fontSize: 28, fontWeight: "bold", color: "#212121" }}>
                {data?.filter(item => item.caseName === "deposit").length || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 14, color: "#666", marginBottom: 4 }}>
                ยอดรวม :
              </div>
              <div style={{ fontSize: 28, fontWeight: "bold", color: "#212121" }}>
                {(data && data.length > 0)
                  ? data.filter(item => item.caseName === "deposit").reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2)
                  : "0.00"}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            padding: "16px 24px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <h2 style={{ fontSize: 18, fontWeight: "bold", marginBottom: 10, color: "#333" }}>Withdraw</h2>
          <div
            style={{
              display: "flex",
              gap: 30,
            }}
          >
            <div>
              <div style={{ fontSize: 14, color: "#666", marginBottom: 4 }}>
                เคสทั้งหมด :
              </div>
              <div style={{ fontSize: 28, fontWeight: "bold", color: "#212121" }}>
                {data?.filter(item => item.caseName === "withdraw").length || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 14, color: "#666", marginBottom: 4 }}>
                ยอดรวม :
              </div>
              <div style={{ fontSize: 28, fontWeight: "bold", color: "#212121" }}>
                {(data && data.length > 0)
                  ? data.filter(item => item.caseName === "withdraw").reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2)
                  : "0.00"}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            padding: "16px 24px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <h2 style={{ fontSize: 18, fontWeight: "bold", marginBottom: 10, color: "#333" }}>ETC.</h2>
          <div
            style={{
              display: "flex",
              gap: 30,
            }}
          >
            <div>
              <div style={{ fontSize: 14, color: "#666", marginBottom: 4 }}>
                เคสทั้งหมด :
              </div>
              <div style={{ fontSize: 28, fontWeight: "bold", color: "#212121" }}>
                {data?.filter(item => item.caseName == "ETC").length || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 14, color: "#666", marginBottom: 4 }}>
                ยอดรวม :
              </div>
              <div style={{ fontSize: 28, fontWeight: "bold", color: "#212121" }}>
                {(data && data.length > 0)
                  ? data.filter(item => item.caseName == "ETC").reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2)
                  : "0.00"}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            background: "#fff",
            borderRadius: 10,
            padding: "16px 24px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <h2 style={{ fontSize: 18, fontWeight: "bold", marginBottom: 10, color: "#333" }}>Panel</h2>
          <div
            style={{
              display: "flex",
              gap: 30,
            }}
          >
            <div>
              <div style={{ fontSize: 14, color: "#666", marginBottom: 4 }}>
                เคสทั้งหมด :
              </div>
              <div style={{ fontSize: 28, fontWeight: "bold", color: "#212121" }}>
                {data?.filter(item => item.caseName == "Panel").length || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 14, color: "#666", marginBottom: 4 }}>
                ยอดรวม :
              </div>
              <div style={{ fontSize: 28, fontWeight: "bold", color: "#212121" }}>
                {(data && data.length > 0)
                  ? data.filter(item => item.caseName == "Panel").reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2)
                  : "0.00"}
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Dashboard Status */}
      <div
        style={{
          display: "flex",
          gap: 12,
          background: "#e0e0e0",
          borderRadius: 10,
          padding: 8,
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        {dashboardData.map((box) => (
          <DashboardBox key={box.status} {...box} />
        ))}
      </div>




      {/* Table Section */}
      <Table
        data={data || []}
        loading={isLoadingAuditReport}
        refetch={handleSearch}
      />
    </div>
  );
};

export default AuditPage;

